/** @format */
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { _$, Checkbox, Dropdown, DropdownMenu, Icon } from '@king-design/react'
import { IconButton } from 'src/components'

export interface ColumnConfigItem {
  key: string
  label: React.ReactNode
}

interface ColumnConfigDropdownProps {
  className?: string
  items: ColumnConfigItem[]
  selectedKeys: string[]
  orderKeys: string[]
  onChange: (nextSelectedKeys: string[], nextOrderKeys: string[]) => void
}

const ColumnConfigDropdown: React.FC<ColumnConfigDropdownProps> = ({
  className,
  items,
  selectedKeys,
  orderKeys,
  onChange
}) => {
  const [draggingKey, setDraggingKey] = useState<string>()
  const itemMap = useMemo(() => new Map(items.map(item => [item.key, item])), [items])
  const panelItems = orderKeys.filter(key => itemMap.has(key))

  const handleToggle = (key: string, checked: boolean) => {
    if (checked) {
      if (!selectedKeys.includes(key)) {
        onChange([...selectedKeys, key], orderKeys)
      }
      return
    }
    onChange(
      selectedKeys.filter(item => item !== key),
      orderKeys
    )
  }

  const handleDrop = (targetKey: string) => {
    if (!draggingKey || draggingKey === targetKey) {
      return
    }
    const nextOrderKeys = [...orderKeys]
    const fromIndex = nextOrderKeys.indexOf(draggingKey)
    const targetIndex = nextOrderKeys.indexOf(targetKey)
    if (fromIndex < 0 || targetIndex < 0) {
      return
    }
    nextOrderKeys.splice(fromIndex, 1)
    nextOrderKeys.splice(targetIndex, 0, draggingKey)
    onChange(selectedKeys, nextOrderKeys)
    setDraggingKey(undefined)
  }

  return (
    <div className={className} id="column-config-dropdown">
      <Dropdown
        trigger="click"
        container="#column-config-dropdown"
        collison="none"
        position={{ my: 'right top+4', at: 'left bottom' }}
      >
        <div>
          <IconButton type="settings" hoverBackground title={_$('列配置')} />
        </div>
        <DropdownMenu className="column-config-menu">
          <div className="column-config-panel">
            {panelItems.map(key => {
              const item = itemMap.get(key)
              if (!item) {
                return null
              }
              const checked = selectedKeys.includes(key)
              return (
                <div
                  key={key}
                  className={`column-config-item ${
                    key === 'TraceId' || key === 'SessionId' ? 'disabled-column' : ''
                  }`}
                  draggable={key !== 'TraceId' && key !== 'SessionId'}
                  onDragStart={() => {
                    setDraggingKey(key)
                  }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => {
                    handleDrop(key)
                  }}
                >
                  <Checkbox
                    value={checked ? '1' : '0'}
                    trueValue="1"
                    disabled={key === 'TraceId' || key === 'SessionId'}
                    falseValue="0"
                    onChangeValue={v => handleToggle(key, v === '1')}
                  >
                    {item.label}
                  </Checkbox>
                  {key !== 'TraceId' && key !== 'SessionId' ? (
                    <Icon className="ion-grid drag-icon" />
                  ) : null}
                </div>
              )
            })}
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  )
}

export default styled(ColumnConfigDropdown)`
  .column-config-menu {
    .column-config-panel {
      width: 180px;
      max-height: 415px;
      overflow-y: auto;
      padding: 8px 0;
    }

    .column-config-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 10px 12px;
      border-bottom: 1px solid #f0f0f0;
      background: #fff;
      cursor: move;
      &.disabled-column {
        cursor: no-drop;
      }
    }

    .drag-icon {
      color: #666;
      cursor: move;
    }
  }
`
